import { useCallback, useEffect, useState } from "react";
import { Database, Download, RotateCcw, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export const ServerSnapshotsPanel = () => {
  const [data, setData] = useState({ snapshots: [], job: { status: "idle" }, nightly: false });
  const [busy, setBusy] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const running = data.job?.status === "running";

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/snapshots");
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load snapshots");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // poll while a snapshot/restore is in flight, then announce the outcome once
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      const res = await api.get("/admin/snapshots").catch(() => null);
      if (!res) return;
      setData(res.data);
      const j = res.data.job;
      if (j.status === "done") {
        toast.success(j.kind === "restore" ? `Restore complete · ${j.detail || ""}` : `Snapshot saved · ${j.file}`);
      } else if (j.status === "error") {
        toast.error(`${j.kind === "restore" ? "Restore" : "Snapshot"} failed: ${j.error}`);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [running]);

  const createSnapshot = async () => {
    setBusy(true);
    try {
      await api.post("/admin/snapshots");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start snapshot");
    } finally {
      setBusy(false);
    }
  };

  const toggleNightly = async () => {
    const next = !data.nightly;
    setData((d) => ({ ...d, nightly: next }));
    try {
      await api.put("/admin/snapshots/schedule", { nightly: next });
      toast.success(next ? "Nightly snapshot ON · runs at midnight (Toronto)" : "Nightly snapshot OFF");
    } catch (e) {
      setData((d) => ({ ...d, nightly: !next }));
      toast.error(e?.response?.data?.detail || "Could not update schedule");
    }
  };

  const download = async (name) => {
    try {
      const res = await api.get(`/admin/snapshots/${encodeURIComponent(name)}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (name) => {
    try {
      await api.delete(`/admin/snapshots/${encodeURIComponent(name)}`);
      toast.success("Snapshot deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const restore = async () => {
    const name = confirmRestore;
    setConfirmRestore(null);
    try {
      await api.post(`/admin/snapshots/${encodeURIComponent(name)}/restore`);
      toast.info("Restore started — hold tight, the list will update when it finishes");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start restore");
    }
  };

  return (
    <div className="space-y-4 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="server-snapshots-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Database className="mt-0.5 h-4 w-4 text-sky-300" />
          <div>
            <p className="text-sm font-semibold text-white">Server database snapshots</p>
            <p className="text-xs text-white/40">
              Dumps <span className="font-semibold text-white/60">every database</span> on this server into
              <span className="font-mono"> {data.dir || "/app/backups/snapshots"}</span> on the VPS. A new snapshot
              replaces the previous one of the same type (manual / nightly). Restore wipes and reloads only
              the collections contained in the snapshot.
            </p>
          </div>
        </div>
        <Button
          onClick={createSnapshot}
          disabled={busy || running}
          data-testid="create-snapshot-btn"
          className="shrink-0 bg-sky-600 text-white hover:bg-sky-500"
        >
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />}
          {running ? (data.job.kind === "restore" ? "Restoring…" : "Snapshotting…") : "Snapshot now"}
        </Button>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2">
        <div>
          <p className="text-xs font-semibold text-white">Nightly snapshot at midnight</p>
          <p className="text-[11px] text-white/40">
            {data.last_nightly_at ? `Last ran ${fmtDate(data.last_nightly_at)}` : "Hasn't run yet"} · America/Toronto
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={data.nightly}
          onClick={toggleNightly}
          data-testid="nightly-snapshot-toggle"
          className={`flex h-6 w-11 shrink-0 items-center rounded-full border p-0.5 transition-colors ${
            data.nightly ? "justify-end border-emerald-400 bg-emerald-500/40" : "justify-start border-white/20 bg-white/10"
          }`}
        >
          <span className="h-4 w-4 rounded-full bg-white shadow-sm" />
        </button>
      </div>

      {data.job?.status === "error" && (
        <p className="flex items-center gap-2 text-xs text-red-300" data-testid="snapshot-job-error">
          <AlertTriangle className="h-3.5 w-3.5" /> Last job failed: {data.job.error}
        </p>
      )}

      <ul className="divide-y divide-white/10 rounded-md border border-white/10" data-testid="snapshot-list">
        {data.snapshots.length === 0 && (
          <li className="px-3 py-3 text-xs text-white/40" data-testid="snapshot-list-empty">No snapshots yet.</li>
        )}
        {data.snapshots.map((s) => (
          <li key={s.name} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid={`snapshot-row-${s.kind}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-xs text-white">{s.name}</p>
              <p className="text-[11px] text-white/40">
                <span className="uppercase tracking-wide text-white/60">{s.kind}</span> · {fmtDate(s.created_at)} · {fmtSize(s.size)}
              </p>
            </div>
            <button type="button" onClick={() => download(s.name)} data-testid={`snapshot-download-${s.kind}`}
              className="inline-flex items-center gap-1 rounded-md border border-sky-400/50 bg-sky-400/10 px-2 py-1.5 text-xs font-semibold text-blue-200 hover:bg-sky-400/20">
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            <button type="button" onClick={() => setConfirmRestore(s.name)} disabled={running} data-testid={`snapshot-restore-${s.kind}`}
              className="inline-flex items-center gap-1 rounded-md border border-amber-400/50 bg-amber-400/10 px-2 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-400/20 disabled:opacity-50">
              <RotateCcw className="h-3.5 w-3.5" /> Restore
            </button>
            <button type="button" onClick={() => remove(s.name)} disabled={running} data-testid={`snapshot-delete-${s.kind}`}
              className="rounded-md border border-white/15 p-1.5 text-white/50 hover:bg-red-500/20 hover:text-red-300 disabled:opacity-50" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>

      <AlertDialog open={!!confirmRestore} onOpenChange={(o) => !o && setConfirmRestore(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0d0d0d] text-white" data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-300" /> Restore this snapshot?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              <span className="font-mono text-xs text-white/80">{confirmRestore}</span>
              <br />Every collection in the snapshot will be <strong>wiped and replaced</strong> with the saved copy —
              anything entered since it was taken is lost. If your login session is newer than the snapshot you may
              need to sign in again. Take a fresh snapshot first if you want a way back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-btn" className="border-white/15 bg-transparent text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={restore} data-testid="restore-confirm-btn" className="bg-amber-500 text-black hover:bg-amber-400">
              Yes, restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ServerSnapshotsPanel;
